import React, { useEffect, useRef } from 'react';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { FormHelperText } from '@mui/material';
import { TextField } from '@mui/material';
import { CircularProgress } from '@mui/material';
import { SharedAccessResourceType } from 'libs/end-to-end/src/auth';
import { SharedAccessState } from 'apps/sigma2-ui/src/lib/hooks/useSharedAccessAuth';
import { useSharedAccessAuth } from 'apps/sigma2-ui/src/lib/hooks/useSharedAccessAuth';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { FullLogoSvg } from 'apps/sigma2-ui/src/components/svg/FullLogo';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Controller } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { z } from 'zod';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';

type Props = {
  resourceId: string;
  resourceType: SharedAccessResourceType;
  onVerified?: () => void;
};

type EmailFormInputs = {
  email: string;
};

type CodeFormInputs = {
  code: string;
};

const EmailSchema = z.string().email();

const SharedAccessAuthFormEmailStep: React.FC<{
  form: ReturnType<typeof useForm<EmailFormInputs>>;
  isLoading: boolean;
  onSubmit: (data: EmailFormInputs) => void;
}> = ({ form, isLoading, onSubmit }) => {
  const language = useLanguage();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const submit = handleSubmit(onSubmit);
  const emailValue = useWatch({
    control,
    name: 'email',
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit(e);
      }}
    >
      <Spacing
        direction="vertical"
        size="gap-4"
        className="w-full items-stretch"
      >
        <Spacing
          direction="vertical"
          size="gap-0"
          className="w-full items-center"
        >
          <S2Typography
            variant="headline-3"
            textColorVariant="purple-900"
            translatedContent={language.sharedAccessEnterEmailHeading()}
            dangerouslyOverrideClassNameFn={(cn) =>
              cn.replace('font-medium', 'font-normal')
            }
          />
          <S2Typography
            variant="body-7"
            textColorVariant="gray-600"
            translatedContent={language.sharedAccessEnterEmailDescription()}
          />
        </Spacing>

        <TextField
          size="small"
          type="email"
          fullWidth
          disabled={isLoading}
          error={!!errors.email}
          helperText={errors.email ? language.inputErrorEmail() : ''}
          {...register('email', {
            validate: (value) => {
              const result = EmailSchema.safeParse(value);
              return result.success || language.inputErrorEmail();
            },
          })}
        />

        <Spacing size="md" direction="vertical" className="justify-center">
          <Button
            size="full-width-small"
            variant="black"
            behavior={{
              type: 'onClick',
              onClick: () => {
                void submit();
              },
            }}
            disabled={isLoading || !emailValue?.trim().length}
          >
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              language.sharedAccessSendCode()
            )}
          </Button>
          <Spacing
            direction="horizontal"
            size="xs"
            className="items-center justify-center"
          >
            <S2Typography
              variant="body-7"
              textColorVariant="gray-600"
              translatedContent={language.sharedAccessAlreadyHaveAnAccount()}
            />
            <TouchTarget
              behavior={{
                type: 'href',
                href: '/login',
              }}
              content={() => (
                <S2Typography
                  variant="body-7-bold"
                  textColorVariant="purple-900"
                  translatedContent={language.sharedAccessAlreadyHaveAnAccountSignIn()}
                />
              )}
            />
          </Spacing>
        </Spacing>
      </Spacing>
    </form>
  );
};

const SharedAccessAuthFormVerifyCodeStep: React.FC<{
  form: ReturnType<typeof useForm<CodeFormInputs>>;
  isLoading: boolean;
  onSubmit: (data: CodeFormInputs) => void;
  status: SharedAccessState;
}> = ({ form, isLoading, onSubmit, status }) => {
  const { control, handleSubmit } = form;
  const language = useLanguage();

  const submit = handleSubmit(onSubmit);
  const lastSubmittedRef = useRef<string | null>(null);

  useEffect(() => {
    if (status.state === 'verify-failed') {
      lastSubmittedRef.current = null;
    }
  }, [status.state]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form onSubmit={submit}>
      <Spacing direction="vertical" size="md" className="w-full items-center">
        <S2Typography
          variant="body-7"
          translatedContent={language.sharedAccessEnterCodeDescription()}
        />

        <Controller
          name="code"
          control={control}
          rules={{
            validate: (value) =>
              /^[0-9]{6}$/.test(value) || language.sharedAccessInvalidCode(),
          }}
          render={({ field, fieldState }) => (
            <div className="w-full flex flex-col items-center">
              <MuiOtpInput
                {...field}
                length={6}
                autoFocus
                TextFieldsProps={{
                  disabled: isLoading,
                }}
                validateChar={(char) => /^[0-9]$/.test(char)}
                onChange={(value) => {
                  field.onChange(value);
                }}
                onComplete={(value) => {
                  if (isLoading) return;
                  if (!/^[0-9]{6}$/.test(value)) return;
                  if (lastSubmittedRef.current === value) return;
                  lastSubmittedRef.current = value;
                  void submit();
                }}
              />

              {fieldState.invalid && (
                <FormHelperText error>
                  {fieldState.error?.message}
                </FormHelperText>
              )}
            </div>
          )}
        />

        {isLoading && <CircularProgress size={32} />}
      </Spacing>
    </form>
  );
};

export const SharedAccessAuthForm: React.FC<Props> = ({
  resourceId,
  resourceType,
  onVerified,
  // eslint-disable-next-line complexity
}) => {
  const language = useLanguage();
  const { status, requestCode, verifyCode, checkAccess } =
    useSharedAccessAuth();

  const emailForm = useForm<EmailFormInputs>();
  const codeForm = useForm<CodeFormInputs>();

  useEffect(() => {
    if (status.state === 'verify-failed') {
      codeForm.reset({ code: '' });
    }
  }, [codeForm, status.state]);

  const isLoading =
    status.state === 'requesting-code' ||
    status.state === 'verifying' ||
    status.state === 'checking-access';

  useEffect(() => {
    checkAccess({ resourceId, resourceType });
  }, [checkAccess, resourceId, resourceType]);

  useEffect(() => {
    if (
      status.state === 'verified' ||
      (status.state === 'access-checked' && status.hasAccess)
    ) {
      onVerified?.();
    }
  }, [status, onVerified]);

  const handleRequestCode = (data: EmailFormInputs) => {
    requestCode({
      email: data.email,
      resourceId,
      resourceType,
    });
  };

  const email = useWatch({
    control: emailForm.control,
    name: 'email',
  });

  const handleVerifyCode = (data: CodeFormInputs) => {
    if (!email) return;

    verifyCode({
      email,
      code: data.code,
      resourceId,
      resourceType,
    });
  };

  return (
    <Card
      as="section"
      className="w-[472px]"
      paddingClassName="px-10 py-8"
      roundedClassName="rounded-4xl"
      shadowClassName="shadow-soft"
    >
      <Spacing
        direction="vertical"
        size="gap-4"
        className="w-full text-purple-900 items-stretch"
      >
        <div className="w-full flex justify-center">
          <FullLogoSvg />
        </div>

        {(status.state === 'request-failed' ||
          (status.state === 'access-checked' && !status.hasAccess)) && (
          <SharedAccessAuthFormEmailStep
            form={emailForm}
            isLoading={isLoading}
            onSubmit={handleRequestCode}
          />
        )}

        {(status.state === 'code-sent' || status.state === 'verify-failed') && (
          <SharedAccessAuthFormVerifyCodeStep
            form={codeForm}
            isLoading={isLoading}
            onSubmit={handleVerifyCode}
            status={status}
          />
        )}

        {status.state === 'verified' && (
          <S2Typography
            variant="body-7"
            translatedContent={language.sharedAccessGranted()}
            textColorVariant="green-800"
          />
        )}

        {(status.state === 'request-failed' ||
          status.state === 'verify-failed') && (
          <S2Typography
            variant="body-7"
            translatedContent={status.errorMessage}
            textColorVariant="red-800"
          />
        )}

        {(status.state === 'code-sent' || status.state === 'verify-failed') && (
          <S2Typography
            variant="body-7"
            translatedContent={language.sharedAccessEnterCodeHelpText()}
            dangerouslyOverrideClassNameFn={(cn) => `${cn} whitespace-nowrap`}
          />
        )}

        {isLoading && (
          <div className="flex justify-center">
            <CircularProgress size={40} />
          </div>
        )}
      </Spacing>
    </Card>
  );
};
